
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { supabase } from "@/lib/supabase";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";

export default function ResetPasswordPage() {
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const { t } = useI18n();
    const { toast } = useToast();
    const [, setLocation] = useLocation();

    useEffect(() => {
        // Check if we have a recovery session
        const checkSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                // If no session and no hash in URL, redirect to login
                if (!window.location.hash.includes("type=recovery") && !window.location.hash.includes("access_token=")) {
                    toast({
                        title: t("common.error"),
                        description: "No active recovery session found.",
                        variant: "destructive",
                    });
                    setLocation("/login");
                }
            }
        };
        checkSession();
    }, [setLocation, t, toast]);

    const handleUpdatePassword = async () => {
        if (!password || password.length < 6) {
            toast({
                title: t("common.error"),
                description: "Password must be at least 6 characters.",
                variant: "destructive",
            });
            return;
        }

        setLoading(true);
        try {
            const { error } = await supabase.auth.updateUser({
                password: password,
            });

            if (error) throw error;

            toast({
                title: t("loginModal.passwordUpdated"),
                duration: 3000,
            });

            // Wait a bit and redirect
            setTimeout(() => {
                setLocation("/account");
            }, 2000);
        } catch (err: any) {
            toast({
                title: t("common.error"),
                description: err.message,
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
            <Card className="w-full max-w-md">
                <CardHeader>
                    <CardTitle>{t("loginModal.resetPassword")}</CardTitle>
                    <CardDescription>
                        {t("loginModal.newPassword")}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Input
                        type="password"
                        placeholder={t("loginModal.password")}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        disabled={loading}
                    />
                    <Button
                        className="w-full"
                        onClick={handleUpdatePassword}
                        disabled={loading || !password}
                    >
                        {loading ? "..." : t("loginModal.updatePassword")}
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}
